/**
 * NOVAPAY-116-R5 — one-time referral exit-rescue (paid post-order only).
 * Frequency: once per order + once per device window (7d).
 */
import { emitEvent } from "./events-v2.js";

const ORDER_KEY = (orderId: string) => `asiastyle.store-v2.referral-exit.${orderId}`;
const GLOBAL_KEY = "asiastyle.store-v2.referral-exit.global";
const WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export type ExitRescueState = {
  shownAt?: number;
  dismissedAt?: number;
  sharedAt?: number;
};

function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

export function wasReferralSharedForOrder(orderId: string): boolean {
  const st = readJson<ExitRescueState>(ORDER_KEY(orderId));
  return Boolean(st?.sharedAt);
}

export function markReferralSharedForOrder(orderId: string): void {
  const st = readJson<ExitRescueState>(ORDER_KEY(orderId)) ?? {};
  st.sharedAt = Date.now();
  writeJson(ORDER_KEY(orderId), st);
  writeJson(GLOBAL_KEY, { lastAt: Date.now() });
  emitEvent("referral_exit_rescue_shared", { order_id: orderId });
  emitEvent("referral_share_completed", { order_id: orderId, source: "exit_rescue" });
}

export function canShowReferralExitRescue(opts: {
  orderId: string;
  paid: boolean;
  alreadyShared: boolean;
}): boolean {
  if (!opts.paid || opts.alreadyShared) return false;
  const st = readJson<ExitRescueState>(ORDER_KEY(opts.orderId));
  if (st?.shownAt || st?.dismissedAt || st?.sharedAt) return false;
  const global = readJson<{ lastAt?: number }>(GLOBAL_KEY);
  if (global?.lastAt && Date.now() - global.lastAt < WINDOW_MS) return false;
  return true;
}

export function markReferralExitDismissed(orderId: string): void {
  const st = readJson<ExitRescueState>(ORDER_KEY(orderId)) ?? {};
  st.dismissedAt = Date.now();
  writeJson(ORDER_KEY(orderId), st);
  writeJson(GLOBAL_KEY, { lastAt: Date.now() });
  emitEvent("referral_exit_rescue_dismissed", { order_id: orderId });
}

export function markReferralExitShown(orderId: string): void {
  const st = readJson<ExitRescueState>(ORDER_KEY(orderId)) ?? {};
  st.shownAt = Date.now();
  writeJson(ORDER_KEY(orderId), st);
  writeJson(GLOBAL_KEY, { lastAt: Date.now() });
  emitEvent("referral_exit_rescue_shown", { order_id: orderId });
}
